/**
 * THE SPAWNED TEMPLATE (v8.373.1) - who a secondary area catches.
 *
 * A weapon's area block may carry one secondary template that the primary's
 * victims spawn: an arc whose last victim bursts, a cone whose victims arc.
 * This module answers the two map questions that raises, purely:
 *
 *   `spawnersOf`      - which of the primary's victims spawn one;
 *   `selectSpawned`   - who each spawned copy catches;
 *   `spawnedOutlines` - the ground each copy covers, for the map's preview.
 *
 * THE RULES (brainstormed 2026-09-20, plan `area-spawn`):
 *   - Anyone CAUGHT spawns, unless they stepped clear of the primary.
 *   - A blast is centred on the spawner, an arc chains from them, a cone or a
 *     beam starts at them and points ONWARD along attacker -> spawner.
 *   - Nobody is struck twice by one shot: `struck` goes in holding the
 *     primary's victims and grows as the spawners are walked in order, so the
 *     first template to reach somebody keeps them.
 *   - The attacker is never caught, nor the spawner by its own template.
 *   - `eligible` is strict. The primary exempts the aimed target from its
 *     `catches`; a secondary is aimed at nobody.
 *
 * Sizes arrive already rolled (`ResolvedSpawn`): one roll per shot, shared by
 * every spawned copy. Zero React, no dice.
 */
import { combatantsInCircle, combatantsInCone, combatantsAlongBeam, arcChain } from './area';
import type { AreaOptions } from './area';
import { bearingBetween } from './facing';
import { DEFAULT_BEAM_WIDTH_M } from '../areaShapes';
import type { ResolvedSpawn, SpawnedBy } from '../areaShapes';
import type { Token } from './tokens';

/** One victim of a spawned template: who, spawned by whom, and the falloff step (1, or the leap of a spawned arc). */
export interface SpawnedVictim {
  id: string;
  spawnerId: string;
  leap: number;
}

export interface SpawnedOptions extends Omit<AreaOptions, 'exclude'> {
  /** Everyone the shot has already struck - the primary's victims. Never mutated. */
  struck: Iterable<string>;
  /** A spawned arc's sight test per leap; the block's `sightPerLeap`. */
  sightPerLeap?: boolean;
}

/**
 * Which victims of the primary spawn the secondary, in victim order.
 * `steppedClear` holds those who passed the avoid test with "steps clear" -
 * the template never touched them, so nothing starts from them. `last` is
 * the highest leap (the aimed target itself when the arc went nowhere).
 */
export function spawnersOf(
  victims: readonly { id: string; leap?: number }[],
  steppedClear: ReadonlySet<string>,
  aimedId: string,
  spawnedBy: SpawnedBy,
): string[] {
  const caught = (id: string) => !steppedClear.has(id);
  if (spawnedBy === 'aimed') return victims.some((v) => v.id === aimedId) && caught(aimedId) ? [aimedId] : [];
  if (spawnedBy !== 'last') return victims.map((v) => v.id).filter(caught);
  const last = lastVictim(victims);
  return last && caught(last.id) ? [last.id] : [];
}

/** The victim furthest down the chain; on a tie, the later one. */
function lastVictim(victims: readonly { id: string; leap?: number }[]): { id: string; leap?: number } | null {
  return victims.reduce<{ id: string; leap?: number } | null>(
    (best, v) => (!best || (v.leap ?? 0) >= (best.leap ?? 0) ? v : best),
    null,
  );
}

/** Who ONE spawned copy catches, before the struck-once rule is applied by the caller's `exclude`. */
function caughtBy(
  spawner: Token,
  attacker: Token | null,
  tokens: readonly Token[],
  spawn: ResolvedSpawn,
  options: AreaOptions & { sightPerLeap?: boolean },
): { id: string; leap: number }[] {
  const origin = { x: spawner.x, z: spawner.z, elevation: spawner.elevation };
  const direct = (ids: string[]) => ids.map((id) => ({ id, leap: 1 }));
  if (spawn.shape === 'blast') return direct(combatantsInCircle(origin, spawn.radius, tokens, options));
  if (spawn.shape === 'arc') {
    return arcChain(spawner, tokens, spawn.reach, spawn.leaps, options).filter((link) => link.leap > 0);
  }
  // ONWARD needs a line to continue: no attacker on the map, no cone and no beam.
  if (!attacker) return [];
  const onward = bearingBetween(attacker, spawner);
  if (spawn.shape === 'cone') {
    return direct(combatantsInCone(origin, onward, spawn.length, spawn.angle, tokens, options));
  }
  if (spawn.shape === 'beam') {
    const halfWidth = (spawn.width > 0 ? spawn.width : DEFAULT_BEAM_WIDTH_M) / 2;
    return direct(combatantsAlongBeam(origin, onward, spawn.length, tokens, options, halfWidth));
  }
  return [];
}

/**
 * Everyone the spawned copies catch, spawner by spawner, in order. A spawner
 * without a token is skipped. Unlike the primary, a spawned cone or beam has
 * no weapon range to fall back on: a `length` of 0 catches nobody.
 */
export function selectSpawned(
  spawnerIds: readonly string[],
  attacker: Token | null,
  tokens: readonly Token[],
  spawn: ResolvedSpawn,
  options: SpawnedOptions,
): SpawnedVictim[] {
  const { struck, ...areaOptions } = options;
  const touched = new Set<string>(struck);
  if (attacker) touched.add(attacker.id);
  const out: SpawnedVictim[] = [];
  for (const spawnerId of spawnerIds) {
    const spawner = tokens.find((t) => t?.id === spawnerId);
    if (!spawner) continue;
    const exclude = new Set([...touched, spawnerId]);
    for (const link of caughtBy(spawner, attacker, tokens, spawn, { ...areaOptions, exclude })) {
      out.push({ id: link.id, spawnerId, leap: link.leap });
      touched.add(link.id);
    }
  }
  return out;
}

/** The ground one spawned copy covers: a closed polygon in world metres, for the overlay to project. */
export interface SpawnedOutline {
  spawnerId: string;
  outline: { x: number; z: number }[];
}

const DEG = Math.PI / 180;
const RING_STEPS = 24;
const WEDGE_STEPS = 16;

function ring(centre: { x: number; z: number }, radius: number): { x: number; z: number }[] {
  return Array.from({ length: RING_STEPS }, (_, i) => {
    const a = (i / RING_STEPS) * Math.PI * 2;
    return { x: centre.x + Math.cos(a) * radius, z: centre.z + Math.sin(a) * radius };
  });
}

function outlineOf(spawner: Token, attacker: Token | null, spawn: ResolvedSpawn): Point[] {
  const outline = OUTLINES.get(spawn.shape);
  return outline ? outline(spawner, attacker, spawn) : [];
}

type Point = { x: number; z: number };
type Outline = (spawner: Token, attacker: Token | null, spawn: ResolvedSpawn) => Point[];

/**
 * A shape that points away from the attacker, along the bearing from
 * them to the spawner - nothing without an attacker or a length.
 */
function aimedFrom(outline: (spawner: Token, axis: number, spawn: ResolvedSpawn) => Point[]): Outline {
  return (spawner, attacker, spawn) =>
    !attacker || !(spawn.length > 0) ? [] : outline(spawner, bearingBetween(attacker, spawner) * DEG, spawn);
}

/** A wedge: the apex, then the arc at full length. */
function coneOutline(spawner: Token, axis: number, spawn: ResolvedSpawn): Point[] {
  if (!(spawn.angle > 0)) return [];
  const half = (Math.min(360, spawn.angle) / 2) * DEG;
  const arc = Array.from({ length: WEDGE_STEPS + 1 }, (_, i) => {
    const a = axis - half + (i / WEDGE_STEPS) * half * 2;
    return { x: spawner.x + Math.cos(a) * spawn.length, z: spawner.z + Math.sin(a) * spawn.length };
  });
  return [{ x: spawner.x, z: spawner.z }, ...arc];
}

/** A rectangle from the spawner out along the axis. */
function beamOutline(spawner: Token, axis: number, spawn: ResolvedSpawn): Point[] {
  const halfWidth = (spawn.width > 0 ? spawn.width : DEFAULT_BEAM_WIDTH_M) / 2;
  const along = { x: Math.cos(axis), z: Math.sin(axis) };
  const side = { x: -along.z * halfWidth, z: along.x * halfWidth };
  const end = { x: spawner.x + along.x * spawn.length, z: spawner.z + along.z * spawn.length };
  return [
    { x: spawner.x + side.x, z: spawner.z + side.z },
    { x: end.x + side.x, z: end.z + side.z },
    { x: end.x - side.x, z: end.z - side.z },
    { x: spawner.x - side.x, z: spawner.z - side.z },
  ];
}

/** Each shape's outline; a shape not listed draws nothing. */
const OUTLINES = new Map<string, Outline>([
  ['blast', (spawner, _attacker, spawn) => (spawn.radius > 0 ? ring(spawner, spawn.radius) : [])],
  ['arc', (spawner, _attacker, spawn) => (spawn.reach > 0 && spawn.leaps > 0 ? ring(spawner, spawn.reach) : [])],
  ['cone', aimedFrom(coneOutline)],
  ['beam', aimedFrom(beamOutline)],
]);

/**
 * One outline per spawner that would actually spawn something - exactly the
 * strip, wedge or ring `selectSpawned` tests, so the preview IS the shot. A
 * spawned arc draws its reach ring; the chain itself is the victims' to show.
 */
export function spawnedOutlines(
  spawnerIds: readonly string[],
  attacker: Token | null,
  tokens: readonly Token[],
  spawn: ResolvedSpawn,
): SpawnedOutline[] {
  return spawnerIds.flatMap((spawnerId) => {
    const spawner = tokens.find((t) => t?.id === spawnerId);
    const outline = spawner ? outlineOf(spawner, attacker, spawn) : [];
    return outline.length ? [{ spawnerId, outline }] : [];
  });
}
